// @services/stubs/StubDocumentAssistantSupplyClient.ts
import type { OptionTs, TaskEitherTs } from "@ps-bridge/interop";
import type { DocumentAssistantSupplyClient } from "@services/clients/DocumentAssistantSupplyClient";
import {
    decodeSupplyAcceptedResponse,
    decodeSupplyStatusResponse,
    supplyLocalError,
    type SupplyAcceptedResponse,
    type SupplyApiDecodeResult,
    type SupplyEmailAttachmentRequest,
    type SupplyError,
    type SupplyErrorCode,
    type SupplyId,
    type SupplyListingId,
    type SupplyManualUploadRequest,
    type SupplyStatusResponse,
} from "@services/clients/DocumentAssistantSupplyApi";
import { taskEitherLeft, taskEitherOf } from "@utils/effects/branded";
import * as Option from "@utils/Option";

export type StubDocumentAssistantSupplyMode =
    | "success"
    | "invalidRequest"
    | "notFound"
    | "conflict"
    | "serviceUnavailable"
    | "networkError"
    | "responseDecodeError";

export const STUB_DOCUMENT_ASSISTANT_SUPPLY_MODE_KEY = "stub:document-assistant-supply:mode";

type SupplyTracePayload = {
    readonly traceId: string;
    readonly requestId: string;
};

const browserStorage = () =>
    typeof window === "undefined" ? Option.none<Storage>() : Option.some(window.localStorage);

export const decodeStubDocumentAssistantSupplyMode = (payload: string): SupplyApiDecodeResult<StubDocumentAssistantSupplyMode> => {
    switch (payload) {
        case "invalidRequest":
        case "notFound":
        case "conflict":
        case "serviceUnavailable":
        case "networkError":
        case "responseDecodeError":
        case "success":
            return { type: "ok", value: payload };
        case "":
            return { type: "ok", value: "success" };
        default:
            return { type: "error", message: "StubDocumentAssistantSupplyMode must be a known failure mode or success" };
    }
};

const readMode = (): StubDocumentAssistantSupplyMode => {
    const value = Option.fold(browserStorage(), () => "", (storage) => storage.getItem(STUB_DOCUMENT_ASSISTANT_SUPPLY_MODE_KEY) ?? "");
    const decoded = decodeStubDocumentAssistantSupplyMode(value);
    return decoded.type === "ok" ? decoded.value : "success";
};

const modeError = (mode: Exclude<StubDocumentAssistantSupplyMode, "success">): SupplyError => {
    const error: SupplyErrorCode = mode;
    switch (mode) {
        case "invalidRequest":
            return supplyLocalError(error, "Supply request is missing a required value.");
        case "notFound":
            return supplyLocalError(error, "Supply status was not found.");
        case "conflict":
            return supplyLocalError(error, "Supply request conflicts with an existing document in this batch.");
        case "serviceUnavailable":
            return supplyLocalError(error, "The document supply service is unavailable.");
        case "networkError":
            return supplyLocalError(error, "The document supply network request failed.");
        case "responseDecodeError":
            return supplyLocalError(error, "Unable to decode document supply response.");
    }
};

const maybeError = <T>(value: SupplyApiDecodeResult<T>): TaskEitherTs<SupplyError, T> => {
    const mode = readMode();
    if (mode !== "success") {
        return taskEitherLeft(modeError(mode));
    }
    if (value.type === "ok") {
        return taskEitherOf(value.value) as TaskEitherTs<SupplyError, T>;
    }
    return taskEitherLeft(supplyLocalError("responseDecodeError", value.message));
};

const traceFor = (supplyId: SupplyId): SupplyTracePayload => ({
    traceId: `stub-supply-trace-${supplyId}`,
    requestId: `stub-supply-request-${supplyId}`,
});

const listingIdPayload = (listingId: OptionTs<SupplyListingId>) =>
    Option.fold(listingId, () => ({}), (value) => ({ listingId: value }));

const acceptedFromManualUpload = (request: SupplyManualUploadRequest): SupplyApiDecodeResult<SupplyAcceptedResponse> => decodeSupplyAcceptedResponse({
    supplyId: request.supplyId,
    batchId: request.batchId,
    displayName: request.displayName,
    sourceId: request.sourceId,
    sourceRevision: {
        sourceId: request.sourceId,
        revision: 1,
    },
    classification: "unknown",
    indexing: "holdForClassification",
    retrieval: "pending",
    uploadStatus: "registeredSource",
    provenance: "userUpload",
    trace: traceFor(request.supplyId),
    ...listingIdPayload(request.listingId),
});

const acceptedFromEmailAttachment = (request: SupplyEmailAttachmentRequest): SupplyApiDecodeResult<SupplyAcceptedResponse> => decodeSupplyAcceptedResponse({
    supplyId: request.supplyId,
    batchId: request.batchId,
    displayName: request.displayName,
    sourceId: request.sourceId,
    sourceRevision: {
        sourceId: request.sourceId,
        revision: 1,
    },
    classification: "emailAttachment",
    indexing: "holdForManualReview",
    retrieval: "pending",
    uploadStatus: "pendingPartyAssociation",
    provenance: "emailAttachment",
    trace: traceFor(request.supplyId),
    ...listingIdPayload(request.listingId),
});

const statusFor = (supplyId: SupplyId): SupplyApiDecodeResult<SupplyStatusResponse> => decodeSupplyStatusResponse({
    supplyId,
    displayName: "Stub supplied document",
    sourceId: `stub-source-${supplyId}`,
    classification: "unknown",
    indexing: "holdForClassification",
    retrieval: "pending",
    uploadStatus: "registeredSource",
    provenance: "userUpload",
    trace: traceFor(supplyId),
});

export class StubDocumentAssistantSupplyClient implements DocumentAssistantSupplyClient {
    manualUpload(request: SupplyManualUploadRequest): TaskEitherTs<SupplyError, SupplyAcceptedResponse> {
        return maybeError(acceptedFromManualUpload(request));
    }

    emailAttachment(request: SupplyEmailAttachmentRequest): TaskEitherTs<SupplyError, SupplyAcceptedResponse> {
        return maybeError(acceptedFromEmailAttachment(request));
    }

    status(supplyId: SupplyId): TaskEitherTs<SupplyError, SupplyStatusResponse> {
        return maybeError(statusFor(supplyId));
    }
}
